import React, { useState, useEffect } from 'react';
import { projectService, activityService } from '../../../utils/supabaseQueries';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const EditProjectModal = ({ isOpen, onClose, project, onProjectUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'film', // Default to Film
    template: 'clean', // Default to Clean
    budget: 'under_500k' // Default to Under 500k
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Updated type options as requested
  const typeOptions = [
    { value: 'film', label: 'Film' },
    { value: 'short_film', label: 'Short Film' },
    { value: 'tv', label: 'TV' },
    { value: 'youtube', label: 'Youtube' },
    { value: 'vertical', label: 'Vertical' }
  ];

  // Updated template options as requested
  const templateOptions = [
    { value: 'clean', label: 'Clean' },
    { value: 'colorful', label: 'Colorful' },
    { value: 'rapid', label: 'Rapid' }
  ];

  // Updated budget options as requested
  const budgetOptions = [
    { value: 'under_500k', label: 'Under 500k' },
    { value: 'under_5m', label: 'Under 5M' },
    { value: 'above_5m', label: 'Above 5M' }
  ];

  // Initialize form data when project changes
  useEffect(() => {
    if (project && isOpen) {
      setFormData({
        name: project?.name || '',
        type: project?.metadata?.type || project?.type || 'film',
        template: project?.metadata?.template || 'clean',
        budget: project?.metadata?.budget || 'under_500k'
      });
      setError(null);
    }
  }, [project, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.name?.trim()) {
      setError('Project name is required');
      return;
    }

    if (!formData?.type) {
      setError('Project type is required');
      return;
    }

    if (!formData?.template) {
      setError('Template is required');
      return;
    }

    if (!formData?.budget) {
      setError('Budget is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare update data
      const updateData = {
        name: formData?.name?.trim(),
        metadata: {
          ...project?.metadata,
          type: formData?.type,
          template: formData?.template,
          budget: formData?.budget
        }
      };

      // Update project
      const { data: updatedProject, error: updateError } = await projectService?.updateProject(
        project?.id, 
        updateData
      );

      if (updateError) throw updateError;

      // Log activity
      await activityService?.logActivity(project?.id, 'project_updated', {
        previousName: project?.name,
        newName: formData?.name,
        updatedFields: ['name', 'type', 'template', 'budget']
      });

      // Notify parent component
      onProjectUpdated?.(updatedProject);
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      setError(error?.message || 'Failed to update project. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        name: '',
        type: 'film',
        template: 'clean',
        budget: 'under_500k'
      });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md shadow-elevation-3">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Edit Project</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Update project details and settings
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
            disabled={isLoading}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-red-500" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Project Name */}
          <div className="space-y-2">
            <label htmlFor="projectName" className="text-sm font-medium text-foreground">
              Project Name *
            </label>
            <Input
              id="projectName"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              placeholder="Enter project name"
              disabled={isLoading}
              required
            />
          </div>

          {/* Project Type */}
          <div className="space-y-2">
            <label htmlFor="projectType" className="text-sm font-medium text-foreground">
              Type *
            </label>
            <Select
              id="projectType"
              value={formData?.type}
              onChange={(value) => handleInputChange('type', value)}
              placeholder="Select project type"
              options={typeOptions}
              disabled={isLoading}
              required
            />
          </div>

          {/* Template */}
          <div className="space-y-2">
            <label htmlFor="projectTemplate" className="text-sm font-medium text-foreground">
              Template *
            </label>
            <Select
              id="projectTemplate"
              value={formData?.template}
              onChange={(value) => handleInputChange('template', value)}
              placeholder="Select template"
              options={templateOptions}
              disabled={isLoading}
              required
            />
          </div>

          {/* Budget */}
          <div className="space-y-2">
            <label htmlFor="projectBudget" className="text-sm font-medium text-foreground">
              Budget *
            </label>
            <Select
              id="projectBudget"
              value={formData?.budget}
              onChange={(value) => handleInputChange('budget', value)}
              placeholder="Select budget range"
              options={budgetOptions}
              disabled={isLoading}
              required
            />
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              loading={isLoading}
              fullWidth
            >
              {isLoading ? 'Updating...' : 'Update Project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProjectModal;